import { memo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import type { BoardLead } from "@school-sales-ai/shared";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import { KebabMenu } from "@/components/KebabMenu";
import { CandidateCountChip } from "@/components/CandidateCountChip";
import { CardLabels } from "@/components/CardLabels";
import { formatElapsed } from "@/lib/format-elapsed";
import { leadBorderClass } from "@/lib/stage-colors";
import { AssigneeBadge } from "@/components/lead-assignment/AssigneeBadge";
import { CHANNEL_SHORT } from "@/lib/channel";

type Props = {
  lead: BoardLead;
  isDragging: boolean;
};

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `hace ${mins}m`;
  return `hace ${formatElapsed(Math.floor(mins / 60))}`;
}

function KanbanCardImpl({ lead, isDragging }: Props): React.JSX.Element {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: lead.id,
  });

  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;

  // Colored left accent when the lead has a color, kept subtle so it doesn't
  // disturb the rest of the card style.
  const accent = lead.color ? `border-l-4 ${leadBorderClass(lead.color)}` : "";

  function handleClick(e: React.MouseEvent<HTMLDivElement>): void {
    // Ignore clicks that originate from buttons or interactive descendants
    // (kebab menu, dropdown, dialog focus-restoration synthetic events).
    if ((e.target as HTMLElement).closest("button, [role='menuitem'], [role='dialog'], [role='alertdialog']")) return;
    navigate(`/leads/${lead.id}`, { state: { from: "/pipeline" } });
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={[
        "rounded-lg border border-ink-100 bg-white p-3 shadow-sm",
        accent,
        "cursor-grab active:cursor-grabbing",
        "flex flex-col gap-2 touch-none",
        isDragging ? "opacity-40" : "hover:border-ink-200 hover:shadow-md",
      ].join(" ")}
    >
      {/* Avatar + name + kebab */}
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold text-ink-600">
          {getInitials(lead.name)}
        </div>
        <span className="flex-1 truncate text-sm font-medium text-ink-900">
          {lead.name ?? <span className="italic text-ink-400">Sin nombre</span>}
        </span>
        <KebabMenu
          leadId={lead.id}
          leadName={lead.name}
          color={lead.color}
          stage={lead.stage}
          assignedProfileId={lead.assignedProfileId}
        />
      </div>

      {/* Score badge + candidate count + channel */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <ScoreBadge score={lead.score} size="xs" showLabel />
          {/* Beside the score because both answer "how much is this worth", and
              a family enrolling two children is the one thing the score does
              not already reflect. */}
          <CandidateCountChip count={lead.candidateCount} />
        </div>
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-ink-400">
          {/* Who works this lead, as initials — only under lead assignment. */}
          <AssigneeBadge assignedProfileId={lead.assignedProfileId} variant="initials" />
          {CHANNEL_SHORT[lead.channelOrigin]}
        </span>
      </div>

      {/* AI situation headline */}
      {lead.summaryHeadline && (
        <p className="truncate text-xs text-ink-500" title={lead.summaryHeadline}>
          {lead.summaryHeadline}
        </p>
      )}

      {/* Labels */}
      <CardLabels leadId={lead.id} labels={lead.labels} />

      {/* Last interaction */}
      <span className="text-xs text-ink-300">
        {formatRelativeTime(lead.lastInteractionAt)}
      </span>
    </div>
  );
}

/**
 * Memoized on the fields the card actually renders.
 *
 * Any mutation on the board invalidates `["pipeline"]`, and the refetch hands
 * back a fresh array of fresh objects — so without a comparator every card on a
 * ~500-lead board re-renders (each rebuilding a dnd-kit registration, two Radix
 * roots and a KebabMenu) even when nothing about it changed.
 */
export const KanbanCard = memo(KanbanCardImpl, (prev, next) => {
  const a = prev.lead;
  const b = next.lead;
  return (
    prev.isDragging === next.isDragging &&
    a.id === b.id &&
    a.name === b.name &&
    a.score === b.score &&
    a.stage === b.stage &&
    a.channelOrigin === b.channelOrigin &&
    a.color === b.color &&
    a.summaryHeadline === b.summaryHeadline &&
    a.candidateCount === b.candidateCount &&
    a.assignedProfileId === b.assignedProfileId &&
    a.lastInteractionAt === b.lastInteractionAt &&
    a.labels.length === b.labels.length &&
    a.labels.every((l, i) => {
      const other = b.labels[i];
      return (
        !!other &&
        l.id === other.id &&
        l.name === other.name &&
        l.color === other.color
      );
    })
  );
});
